import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiSimCard2Line,
  RiInformationLine,
  RiHistoryLine,
  RiDeleteBinLine,
  RiLinksLine,
  RiAlertLine,
  RiLoader4Line,
  RiPriceTag3Line,
} from 'react-icons/ri';
import simService, { Sim, SimStatus, SimType, SimUsage } from '../../services/simService';
import simPackageService from '../../services/simPackageService';
import userService from '../../services/userService';
import { useDeleteSim } from '../../hooks/mutations/useSimMutations';
import { usePermission } from '../../hooks/usePermission';
import { SIMS_PATH, simEditUrl, deviceDetailUrl } from '../../utils/deviceLinks';
import { parseRouteId } from '../../utils/routeId';
import { showConfirm } from '../../utils/alert';
import { formatDate, formatDateTimeFull } from '../../utils/dateUtils';
import { getSimStatusLabel, getSimTypeLabel } from '../../utils/simHelpers';
import { ItemRentalHistoryCard, ItemReturnItemsCard, ItemStockIssuesCard } from '../../components/device/ItemRelatedCards';
import { SIM_FIELD_LABELS, SIM_VALUE_FORMATTERS, packagePriceLabel } from '../../components/sim/simForm';
import { simDetailKey } from './SimFormPage';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

/** สีป้ายสถานะซิม — ชุดเดียวกับตารางหน้ารายการ (ISSUED ม่วง · RESERVED accent · ที่เหลือ fallback แดง) */
export const simStatusBadgeClass = (value: string): string =>
  value === SimStatus.AVAILABLE ? 'bg-success-100 text-success-800' :
  value === SimStatus.IN_USE ? 'bg-primary-100 text-primary-800' :
  value === SimStatus.ACTIVE ? 'bg-success-100 text-success-800' :
  value === SimStatus.INACTIVE ? 'bg-secondary-100 text-secondary-800' :
  value === SimStatus.SUSPENDED ? 'bg-warning-100 text-warning-800' :
  value === SimStatus.RESERVED ? 'bg-accent-100 text-accent-800' :
  value === SimStatus.ISSUED ? 'bg-purple-100 text-purple-700' :
  'bg-danger-100 text-danger-800';

const RENTAL_STATUS_TH: Record<string, string> = {
  ACTIVE: 'เช่าใช้อยู่',
  PENDING_CANCELLATION: 'รอยกเลิก',
};

/** ข้อมูลที่ GET /sims/{id}/usage ส่งเพิ่ม (ใบเบิก-ยืม) — ยังไม่อยู่ใน type กลาง */
type SimUsageWithIssue = SimUsage & {
  current_stock_issue_id?: number | null;
  current_stock_issue_number?: string | null;
  issued_to_display?: string | null;
};

/**
 * หน้ารายละเอียดซิม — `/master-data/sims/:id` (แทนโมดัล "รายละเอียดซิมการ์ด" · มติ PO 28 ต.ค. 2026)
 * ครบทุกช่องของฟอร์มเดิม + "การใช้งานปัจจุบัน" (GET /sims/{id}/usage · Tiwakorn #309) + การ์ดที่เกี่ยวข้อง
 * (สัญญาเช่าที่เคยอยู่ · ใบตามเก็บ · ใบเบิก-ยืม) + ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม
 * "ใครถือซิมอยู่" มาจาก backend (usage.current / rental_hold / ใบเบิก) — ไม่ใช่ sims.status ที่ cron พลิกได้
 */
const SimViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const simId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(SIMS_PATH);
  const deleteSimMutation = useDeleteSim();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: sim, isLoading, error, refetch } = useQuery({
    queryKey: simDetailKey(simId ?? 0),
    queryFn: () => simService.getSim(simId!),
    enabled: simId !== null,
  });
  // การใช้งานปัจจุบัน — พังก็แค่แสดงข้อความ ไม่ทำให้หน้าพัง (เหมือนโมดัลเดิม)
  const usageQuery = useQuery({
    queryKey: [...simDetailKey(simId ?? 0), 'usage'],
    queryFn: () => simService.getSimUsage(simId!) as Promise<SimUsageWithIssue>,
    enabled: simId !== null && !!sim,
  });
  // ชื่อผู้ลงทะเบียน (registered_by_user_id → ชื่อ) — รายการเดียวกับที่ฟอร์มใช้
  // GET /users/performers รับ sims.view (PERFORMER_PICKER_PERMISSIONS) · หา user ไม่เจอ = แสดง registration_name / #id
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'performers'],
    queryFn: () => userService.getPerformers(),
    enabled: !!sim,
  });
  const { data: simPackages = [] } = useQuery({ queryKey: ['simPackages', 'active'], queryFn: () => simPackageService.getActiveSimPackages(), enabled: !!sim });

  const hereState = detailNavState(location);

  const handleRefresh = () => {
    refetch();
    usageQuery.refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = () => {
    if (!sim) return;
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบซิมการ์ด ${sim.phone_number} ใช่หรือไม่?`,
      async () => {
        try {
          await deleteSimMutation.mutateAsync(sim.id);
          goTo(backTo, { replace: true });
        } catch (e) {
          console.error('Error deleting sim:', e); // useMutationWithRefresh แสดงข้อความจาก server แล้ว
        }
      },
      { isDanger: true }
    );
  };

  const notFound = simId === null ? { response: { status: 404 } } : null;
  const registrant = sim?.registered_by_user_id
    ? (() => {
        const u = users.find((x) => x.id === sim.registered_by_user_id);
        return u ? (u.full_name || u.username) : (sim.registration_name || `#${sim.registered_by_user_id}`);
      })()
    : sim?.registration_name || null;
  const pkg = sim?.sim_package_id ? simPackages.find((p) => p.id === sim.sim_package_id) : undefined;

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'ซิมการ์ด', path: backTo }, { label: sim?.phone_number || 'รายละเอียด' }]}
      icon={<RiSimCard2Line />}
      title={sim?.phone_number || 'รายละเอียดซิมการ์ด'}
      recordId={sim?.code}
      subtitle={sim ? [sim.operator, getSimTypeLabel(sim.sim_type)].filter(Boolean).join(' · ') : undefined}
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={sim ? simEditUrl(sim.id) : undefined}
      editPermission="sims.edit"
      editState={navState}
      actions={
        sim && hasPermission('sims.delete') ? (
          <button type="button" onClick={handleDelete} className={cn(headerButtonClass, headerButtonVariants.danger)}>
            <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
            ลบ
          </button>
        ) : undefined
      }
      loading={simId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบซิมที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        sim && (
          <>
            <RecordMetaCard createdAt={sim.created_at} createdBy={sim.created_by} updatedAt={sim.updated_at} updatedBy={sim.updated_by} />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="sim"
                entityId={sim.id}
                refreshTrigger={activityRefresh}
                fieldLabels={SIM_FIELD_LABELS}
                valueFormatters={SIM_VALUE_FORMATTERS}
              />
            </DetailSection>
          </>
        )
      }
    >
      {sim && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐาน"
            icon={<RiInformationLine />}
            actions={
              <span className={cn('inline-flex px-2 py-1 text-xs font-semibold rounded-full', simStatusBadgeClass(sim.status))} data-testid="sim-status-badge">
                {getSimStatusLabel(sim.status)}
              </span>
            }
          >
            <InfoGrid>
              <InfoField label="รหัสซิม" value={sim.code} mono />
              <InfoField label="เบอร์โทรศัพท์" value={sim.phone_number} mono />
              <InfoField label="ค่ายมือถือ" value={sim.operator} />
              <InfoField label="ประเภทซิม" value={getSimTypeLabel(sim.sim_type)} />
              <InfoField label="Serial (ICCID)" value={sim.serial_number} mono />
              <InfoField label="รหัส Activate" value={sim.activation_code} mono />
              <InfoField label="ผู้ลงทะเบียน" value={registrant} />
              <InfoField label="วันที่เปิดใช้งาน" value={sim.activation_date ? formatDate(sim.activation_date) : null} />
              {/* ซิมรายเดือนไม่มีวันหมดอายุ — แสดงเมื่อมีค่า (แถว legacy ที่ยังค้าง) หรือเป็นประเภทที่มีวันหมดอายุ */}
              {(sim.sim_type !== SimType.POSTPAID || sim.expiry_date) && (
                <InfoField label="วันหมดอายุ" value={sim.expiry_date ? formatDateTimeFull(sim.expiry_date) : null} />
              )}
              <InfoField label="หมายเหตุ" value={sim.notes} multiline fullWidth />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="แพ็กเกจซิม" icon={<RiPriceTag3Line />}>
            <InfoGrid columns={3}>
              <InfoField
                label="แพ็กเกจ"
                value={sim.package_name || (sim.sim_package_id ? `แพ็กเกจ #${sim.sim_package_id}` : null)}
                hint={sim.sim_package_id && !pkg ? 'แพ็กเกจนี้ถูกปิดใช้งานแล้ว' : undefined}
              />
              <InfoField label="ความเร็ว" value={sim.speed_mbps ? `${sim.speed_mbps} Mbps` : null} />
              <InfoField label="โควต้า" value={pkg ? (pkg.data_quota_gb ? `${pkg.data_quota_gb} GB` : 'Unlimited') : null} />
              <InfoField label="รอบบิล" value={sim.billing_cycle ? (String(sim.billing_cycle) === 'YEARLY' ? 'รายปี' : 'รายเดือน') : null} />
              <InfoField
                label="ค่าบริการ"
                value={
                  pkg ? packagePriceLabel(pkg)
                    : String(sim.billing_cycle) === 'YEARLY' && sim.yearly_cost != null ? `฿${Number(sim.yearly_cost).toLocaleString()}/ปี`
                    : sim.monthly_fee != null ? `฿${Number(sim.monthly_fee).toLocaleString()}/เดือน` : null
                }
              />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="การใช้งานปัจจุบัน" icon={<RiLinksLine />} testId="sim-usage-section">
            <SimUsageBody sim={sim} usage={usageQuery.data} loading={usageQuery.isLoading} failed={usageQuery.isError} />
          </DetailSection>

          <ItemRentalHistoryCard kind="sim" itemId={sim.id} rowState={hereState} />
          <ItemReturnItemsCard kind="sim" itemId={sim.id} />
          <ItemStockIssuesCard kind="sim" itemId={sim.id} rowState={hereState} />
        </>
      )}
    </DetailPageLayout>
  );
};

/** เนื้อหา "การใช้งานปัจจุบัน" — ยกมาจาก renderSimUsageBody ของโมดัลเดิม (Tiwakorn #309 · fix ค · ยืมซิม) */
const SimUsageBody: React.FC<{ sim: Sim; usage?: SimUsageWithIssue; loading: boolean; failed: boolean }> = ({ sim, usage, loading, failed }) => {
  const simUsage = usage;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-secondary-500" data-testid="sim-usage-loading">
        <RiLoader4Line className="w-4 h-4 animate-spin" />
        <span>กำลังโหลดข้อมูลการใช้งาน...</span>
      </div>
    );
  }
  if (failed || !simUsage) {
    return <div className="text-sm text-danger-600" data-testid="sim-usage-error">โหลดข้อมูลการใช้งานไม่สำเร็จ</div>;
  }
  const cur = simUsage.current;
  const extras = (
    <>
      {simUsage.status_mismatch && (
        <div className="mb-3 flex items-start gap-2 p-3 rounded-lg border border-warning-200 bg-warning-50 text-warning-800 text-sm" data-testid="sim-usage-mismatch">
          <RiAlertLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>สถานะซิมไม่สอดคล้องกับสัญญา — ข้อมูลอาจค้าง กรุณาตรวจสอบ</span>
        </div>
      )}
      {simUsage.has_pending_return && (
        <div className="mb-3 flex items-start gap-2 p-3 rounded-lg border border-warning-200 bg-warning-50 text-warning-800 text-sm" data-testid="sim-usage-pending-return">
          <RiAlertLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            ⚠ รอรับคืน{(simUsage.pending_return_count || 0) > 1 ? ` (${simUsage.pending_return_count})` : ''} — ยังมีรายการตามเก็บที่ยังไม่ปิด
            ซิมอาจยังอยู่กับลูกค้า · ระบบจะไม่ให้นำซิมนี้ไปทำสัญญาใหม่จนกว่าจะบันทึกรับคืน หรือยกเลิกรายการตามเก็บ
          </span>
        </div>
      )}
      {String(sim.status) === SimStatus.ISSUED && (
        <div className="mb-3 p-3 rounded-lg border border-purple-200 bg-purple-50 text-purple-800 text-sm" data-testid="sim-issued-holder">
          เบิกไปใช้ภายใน — ผู้ยืม: <span className="font-semibold">{simUsage.issued_to_display || sim.issued_to_display || '-'}</span>
          {(simUsage.current_stock_issue_number || sim.current_stock_issue_number) && (
            <>
              {' · ใบเบิก '}
              {simUsage.current_stock_issue_id ? (
                <Link to={`/stock/pending-returns?issue=${simUsage.current_stock_issue_id}`} className="font-mono underline">
                  {simUsage.current_stock_issue_number}
                </Link>
              ) : (
                <span className="font-mono">{simUsage.current_stock_issue_number || sim.current_stock_issue_number}</span>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
  if (!simUsage.in_use || !cur) {
    // fix ค: "ว่าง" ตามการใช้งานปัจจุบัน แต่ยังมีสัญญาที่ยังไม่จบผูกอยู่ (rental_hold) — ต้องบอกเหตุผล (เคสจริง: ซิม 0834965065)
    const hold = simUsage.rental_hold;
    return (
      <>
        {extras}
        {hold && (
          <div className="mb-3 flex items-start gap-2 p-3 rounded-lg border border-info-200 bg-info-50 text-info-800 text-sm" data-testid="sim-usage-rental-hold">
            <RiAlertLine className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <Link to={`/rentals/view/${hold.rental_id}`} className="font-semibold underline">🔒 ผูกกับสัญญา {hold.rental_number || `#${hold.rental_id}`}</Link>
              {' — '}{hold.message}
            </span>
          </div>
        )}
        <div className="text-sm text-secondary-600" data-testid="sim-usage-idle">ไม่ได้ผูกกับสัญญาเช่าใด (ว่าง)</div>
      </>
    );
  }
  const deviceLabel = cur.device_id
    ? `${cur.device_code || '-'}${cur.device_serial_number ? ` – ${cur.device_serial_number}` : ''}${cur.device_model ? ` (${cur.device_model})` : ''}`
    : null;
  return (
    <>
      {extras}
      <InfoGrid>
        <InfoField label="สัญญาเช่า">
          <Link to={`/rentals/view/${cur.rental_id}`} className="font-medium text-primary-600 hover:underline" data-testid="sim-usage-rental-link">
            {cur.rental_number || `#${cur.rental_id}`}
          </Link>
        </InfoField>
        <InfoField label="สถานะสัญญา" value={RENTAL_STATUS_TH[cur.rental_status || ''] || cur.rental_status || null} />
        <InfoField label="บริษัท" value={cur.company_name} />
        <InfoField label="โครงการ" value={cur.site_name} />
        <InfoField label="อุปกรณ์ที่ผูก" fullWidth>
          {deviceLabel ? (
            <Link to={deviceDetailUrl(cur.device_id)} className="font-medium text-primary-600 hover:underline" data-testid="sim-usage-device-link">
              {deviceLabel}
            </Link>
          ) : (
            <span>ไม่ได้ผูกอุปกรณ์</span>
          )}
        </InfoField>
        <InfoField label="ใช้ตั้งแต่" value={cur.since ? formatDateTimeFull(cur.since) : null} />
      </InfoGrid>
    </>
  );
};

export default SimViewPage;
